import React, { useCallback, useEffect, useState } from 'react';
import { API } from '../api';
import { formatCount, formatMoney } from '../utils/adminFormatters';
import './PromoTariffsTab.css';

const EMPTY_FORM = { name: '', description: '', roublePrice: '', hours: '', minutes: '0', expiresAt: '', active: true };

const MOSCOW_TIME_ZONE = 'Europe/Moscow';

const toMoscowInputValue = (dateTime) => {
  if (!dateTime) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MOSCOW_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(new Date(dateTime)).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const moscowInputToIso = (value) => value ? new Date(`${value}:00+03:00`).toISOString() : null;

const durationToForm = (msAmount) => {
  const totalMinutes = Math.round(Number(msAmount) / 60000);
  return { hours: String(Math.floor(totalMinutes / 60)), minutes: String(totalMinutes % 60) };
};

const PromoTariffsTab = () => {
  const [tariffs, setTariffs] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadTariffs = useCallback(async () => {
    setLoading(true);
    const result = await API.tariffs.list();
    if (result.success) {
      setTariffs(Array.isArray(result.data) ? result.data : []);
      setError('');
    } else {
      setError(result.error || 'Не удалось загрузить промо-тарифы');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadTariffs();
  }, [loadTariffs]);

  const updateForm = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      roublePrice: Number(form.roublePrice),
      hours: Number(form.hours || 0),
      minutes: Number(form.minutes || 0),
      expiresAt: moscowInputToIso(form.expiresAt)
    };
    const result = editingId
      ? await API.tariffs.update(editingId, payload)
      : await API.tariffs.create(payload);
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'Не удалось сохранить промо-тариф');
      return;
    }
    resetForm();
    await loadTariffs();
  };

  const edit = (tariff) => {
    setEditingId(tariff.id);
    setForm({
      name: tariff.name,
      description: tariff.description,
      roublePrice: String(tariff.roublePrice),
      ...durationToForm(tariff.msAmount),
      expiresAt: toMoscowInputValue(tariff.expiresAt),
      active: tariff.active
    });
  };

  const remove = async (tariff) => {
    if (!window.confirm(`Скрыть промо-тариф «${tariff.name}»? История продаж сохранится.`)) return;
    const result = await API.tariffs.remove(tariff.id);
    if (!result.success) {
      setError(result.error || 'Не удалось удалить промо-тариф');
      return;
    }
    await loadTariffs();
  };

  return (
    <div className="promo-tariffs tab-content">
      <div className="promo-tariffs-heading">
        <div>
          <h2>Промо-тарифы</h2>
          <p>Промо автоматически появляется на страницах оплаты и участвует в статистике продаж.</p>
        </div>
      </div>

      <form className="promo-tariff-form" onSubmit={submit}>
        <h3>{editingId ? 'Изменить промо-тариф' : 'Новый промо-тариф'}</h3>
        <div className="promo-form-grid">
          <label>Название<input required name="name" value={form.name} onChange={updateForm} maxLength="120" /></label>
          <label>Стоимость, ₽<input required name="roublePrice" value={form.roublePrice} onChange={updateForm} type="number" min="0.01" step="0.01" /></label>
          <label>Часы<input required name="hours" value={form.hours} onChange={updateForm} type="number" min="0" step="1" /></label>
          <label>Минуты<input required name="minutes" value={form.minutes} onChange={updateForm} type="number" min="0" max="59" step="1" /></label>
          <label>Действует до (МСК)<input required name="expiresAt" value={form.expiresAt} onChange={updateForm} type="datetime-local" /></label>
        </div>
        <label>Описание<textarea required name="description" value={form.description} onChange={updateForm} maxLength="1000" rows="3" /></label>
        <label className="promo-active"><input name="active" checked={form.active} onChange={updateForm} type="checkbox" /> Показывать на витрине</label>
        <div className="promo-form-actions">
          <button type="submit" disabled={saving}>{saving ? 'Сохранение…' : editingId ? 'Сохранить' : 'Создать'}</button>
          {editingId ? <button type="button" className="secondary" onClick={resetForm}>Отменить</button> : null}
        </div>
      </form>

      {error ? <div className="promo-error" role="alert">{error}</div> : null}
      {loading ? <div className="loading-state"><div className="spinner"></div><p>Загрузка промо-тарифов...</p></div> : null}
      {!loading && tariffs.length === 0 ? <div className="empty-message">Промо-тарифов пока нет</div> : null}
      {!loading && tariffs.length > 0 ? <div className="promo-tariffs-grid">
        {tariffs.map((tariff) => (
          <article className="promo-tariff-card" key={tariff.id}>
            <div className="promo-tariff-card-header"><h3>{tariff.name}</h3><span className={tariff.active ? 'promo-status active' : 'promo-status'}>{tariff.active ? 'Активен' : 'Скрыт'}</span></div>
            <p>{tariff.description}</p>
            <div className="promo-tariff-price">{formatMoney(tariff.roublePrice, 'RUB')}</div>
            <div className="promo-tariff-duration">{durationToForm(tariff.msAmount).hours} ч {durationToForm(tariff.msAmount).minutes} мин</div>
            <div className="promo-tariff-expires">До {tariff.expiresAt ? new Intl.DateTimeFormat('ru-RU', { timeZone: MOSCOW_TIME_ZONE, dateStyle: 'medium', timeStyle: 'short' }).format(new Date(tariff.expiresAt)) : 'не указано' } МСК</div>
            <dl className="promo-tariff-stats"><div><dt>Продажи</dt><dd>{formatCount(tariff.statistics?.purchasesCount || 0)}</dd></div><div><dt>Выручка</dt><dd>{formatMoney(tariff.statistics?.revenueRub || 0, 'RUB')}</dd></div></dl>
            <div className="promo-card-actions"><button onClick={() => edit(tariff)}>Изменить</button><button className="danger" onClick={() => remove(tariff)}>Удалить</button></div>
          </article>
        ))}
      </div> : null}
    </div>
  );
};

export default PromoTariffsTab;
